import React, { useState, useRef, useEffect } from 'react';
import { X, Lightbulb, HelpCircle, Check, AlertCircle } from 'lucide-react';
import { classifySentence, validateClassification, splitIntoSentences, type BucketType, type ClassificationResult } from './classification';
import { suggestLikelyBucket } from './llmService';
import { useAiCapability } from '../../lib/ai/useAiCapability';

interface SentenceClassifierProps {
  isOpen: boolean;
  onClose: () => void;
  initialSentence?: string;
  onInsert?: (sentence: string, bucket: BucketType) => void;
}

interface SentenceCard {
  id: string;
  text: string;
  bucket: BucketType | null;
  classification: ClassificationResult | null;
}

export const SentenceClassifier: React.FC<SentenceClassifierProps> = ({
  isOpen,
  onClose,
  initialSentence = '',
  onInsert
}) => {
  const [inputText, setInputText] = useState(initialSentence);
  const [sentenceCards, setSentenceCards] = useState<SentenceCard[]>([]);
  const [draggedCard, setDraggedCard] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [llmSuggestion, setLlmSuggestion] = useState<ClassificationResult | null>(null);
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const outcomeDriverAiEnabled = useAiCapability('outcome_driver_generator');

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (initialSentence) {
      setInputText(initialSentence);
    }
  }, [initialSentence]);

  const handleInputChange = (text: string) => {
    setInputText(text);
    // Auto-split if multiple sentences detected
    if (text.includes('.') || text.includes('!') || text.includes('?')) {
      const sentences = splitIntoSentences(text);
      if (sentences.length > 1) {
        setSentenceCards(sentences.map((s, idx) => ({
          id: `card-${idx}`,
          text: s,
          bucket: null,
          classification: null
        })));
      }
    }
  };

  const handleCreateCard = () => {
    if (!inputText.trim()) return;

    const sentences = splitIntoSentences(inputText);
    if (sentences.length > 1) {
      // Multi-sentence mode
      setSentenceCards(sentences.map((s, idx) => ({
        id: `card-${Date.now()}-${idx}`,
        text: s,
        bucket: null,
        classification: null
      })));
    } else {
      // Single sentence
      const card: SentenceCard = {
        id: `card-${Date.now()}`,
        text: inputText.trim(),
        bucket: null,
        classification: null
      };
      setSentenceCards([card]);
    }
    setInputText('');
  };

  const handleSuggestBucket = async () => {
    if (sentenceCards.length === 0) {
      const text = inputText.trim() || initialSentence;
      if (!text) return;

      setIsLoadingSuggestion(true);
      try {
        const suggestion = await suggestLikelyBucket(text);
        setLlmSuggestion(suggestion);
      } catch (error) {
        console.warn('LLM suggestion unavailable, using rule-based classification');
        const classification = classifySentence(text);
        setLlmSuggestion(classification);
      } finally {
        setIsLoadingSuggestion(false);
      }
    } else {
      // Suggest for first unclassified card
      const unclassified = sentenceCards.find(c => !c.bucket);
      if (unclassified) {
        setIsLoadingSuggestion(true);
        try {
          const suggestion = await suggestLikelyBucket(unclassified.text);
          setLlmSuggestion(suggestion);
        } catch (error) {
          const classification = classifySentence(unclassified.text);
          setLlmSuggestion(classification);
        } finally {
          setIsLoadingSuggestion(false);
        }
      }
    }
  };

  const handleDrop = (bucket: BucketType, cardId?: string) => {
    const cardIdToMove = cardId || draggedCard;
    if (!cardIdToMove) return;

    const card = sentenceCards.find(c => c.id === cardIdToMove);
    if (!card) return;

    const validation = validateClassification(card.text, bucket);
    
    setSentenceCards(sentenceCards.map(c => 
      c.id === cardIdToMove 
        ? { ...c, bucket, classification: validation }
        : c
    ));
    setDraggedCard(null);
    setSelectedCard(null);
  };

  const handleTapSelect = (bucket: BucketType, cardId: string) => {
    handleDrop(bucket, cardId);
  };

  const handleInsertAll = () => {
    if (!onInsert) return;
    
    sentenceCards.forEach(card => {
      if (card.bucket) {
        onInsert(card.text, card.bucket);
      }
    });
    onClose();
  };

  const handleInsertSelected = (cardId: string) => {
    if (!onInsert) return;
    
    const card = sentenceCards.find(c => c.id === cardId);
    if (card && card.bucket) {
      onInsert(card.text, card.bucket);
      onClose();
    }
  };

  if (!isOpen) return null;

  const activeCard = sentenceCards.find(c => c.id === selectedCard || c.id === draggedCard);
  const allClassified = sentenceCards.length > 0 && sentenceCards.every(c => c.bucket);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[var(--bg-raised)] border-b border-charcoal-200 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-charcoal-900">Classify a Sentence</h2>
            <p className="text-sm text-charcoal-600 mt-1">
              Drag the card below into the bucket that feels right
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-charcoal-400 hover:text-charcoal-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Input Section */}
          <div>
            <label className="block text-sm font-semibold text-charcoal-900 mb-2">
              Paste or type a sentence
            </label>
            <textarea
              value={inputText}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={'e.g., "We have no shared understanding of how Wes\'s work connects to revenue-producing engagements."'}
              className="w-full min-h-[100px] p-4 border border-charcoal-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={handleCreateCard}
                disabled={!inputText.trim()}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-charcoal-300 disabled:cursor-not-allowed text-white font-medium transition-colors"
              >
                Create Card
              </button>
              {outcomeDriverAiEnabled && (
                <button
                  onClick={handleSuggestBucket}
                  disabled={isLoadingSuggestion || (!inputText.trim() && sentenceCards.length === 0)}
                  className="px-4 py-2 bg-sage-600 hover:bg-sage-700 disabled:bg-charcoal-300 disabled:cursor-not-allowed text-white font-medium transition-colors flex items-center gap-2"
                >
                  <Lightbulb className="h-4 w-4" />
                  {isLoadingSuggestion ? 'Analyzing...' : 'Suggest Likely Bucket'}
                </button>
              )}
            </div>

            {/* LLM Suggestion Display */}
            {llmSuggestion && (
              <div className="mt-4 p-4 bg-primary-50 border border-primary-200">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="h-5 w-5 text-primary-600" />
                  <span className="font-semibold text-charcoal-900">
                    Likely Bucket: {llmSuggestion.bucket === 'current-state' ? 'Current State' : 
                                   llmSuggestion.bucket === 'problem-opportunity' ? 'Problem / Opportunity' : 
                                   'Ramifications'}
                  </span>
                </div>
                <p className="text-sm text-charcoal-700">{llmSuggestion.reasoning}</p>
              </div>
            )}
          </div>

          {/* Sentence Cards */}
          {sentenceCards.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-charcoal-900">Sentence Cards</h3>
              {sentenceCards.map(card => (
                <SentenceCardComponent
                  key={card.id}
                  card={card}
                  isMobile={isMobile}
                  onDragStart={() => setDraggedCard(card.id)}
                  onDragEnd={() => setDraggedCard(null)}
                  onSelect={() => setSelectedCard(card.id)}
                  onInsert={handleInsertSelected}
                />
              ))}
            </div>
          )}

          {/* Drop Zones */}
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            <BucketDropZone
              bucket="current-state"
              label="Current State"
              description="Facts and observations"
              onDrop={handleDrop}
              onTapSelect={isMobile && selectedCard ? () => handleTapSelect('current-state', selectedCard) : undefined}
              isMobile={isMobile}
            />
            <BucketDropZone
              bucket="problem-opportunity"
              label="Problem / Opportunity"
              description="What the facts mean"
              onDrop={handleDrop}
              onTapSelect={isMobile && selectedCard ? () => handleTapSelect('problem-opportunity', selectedCard) : undefined}
              isMobile={isMobile}
            />
            <BucketDropZone
              bucket="ramifications"
              label="Ramifications"
              description="Why it matters"
              onDrop={handleDrop}
              onTapSelect={isMobile && selectedCard ? () => handleTapSelect('ramifications', selectedCard) : undefined}
              isMobile={isMobile}
            />
          </div>

          {/* Feedback Panel */}
          {activeCard?.classification && (
            <div className={`mt-6 p-4  border-2 ${
              activeCard.classification.isCorrect 
                ? 'bg-[var(--green-dim)] border-[var(--green)]' 
                : activeCard.classification.isCorrect === false
                ? 'bg-[var(--mustard-dim)] border-[var(--mustard)]'
                : 'bg-primary-50 border-primary-200'
            }`}>
              <div className="flex items-start gap-3">
                {activeCard.classification.isCorrect ? (
                  <Check className="h-5 w-5 text-[var(--green)] mt-0.5" />
                ) : activeCard.classification.isCorrect === false ? (
                  <AlertCircle className="h-5 w-5 text-[var(--mustard)] mt-0.5" />
                ) : (
                  <HelpCircle className="h-5 w-5 text-primary-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="font-semibold text-charcoal-900 mb-1">
                    {activeCard.classification.feedback || activeCard.classification.reasoning}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-4 pt-4 border-t border-charcoal-200">
            {allClassified && onInsert && (
              <button
                onClick={handleInsertAll}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold transition-colors"
              >
                Insert All into Correct Fields
              </button>
            )}
            <button
              onClick={onClose}
              className="px-6 py-3 bg-charcoal-600 hover:bg-charcoal-700 text-white font-semibold transition-colors"
            >
              Return to Editing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sentence Card Component
const SentenceCardComponent: React.FC<{
  card: SentenceCard;
  isMobile: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onSelect: () => void;
  onInsert?: (cardId: string) => void;
}> = ({ card, isMobile, onDragStart, onDragEnd, onSelect, onInsert }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={cardRef}
      draggable={!isMobile}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={isMobile ? onSelect : undefined}
      className={`p-4 border-2  cursor-move ${
        card.bucket 
          ? card.classification?.isCorrect 
            ? 'border-[var(--green)] bg-[var(--green-dim)]' 
            : 'border-[var(--mustard)] bg-[var(--mustard-dim)]'
          : 'border-charcoal-200 bg-[var(--bg-raised)] hover:border-primary-300'
      }`}
    >
      <p className="text-charcoal-900 mb-2">{card.text}</p>
      {card.bucket && (
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs font-medium text-charcoal-600">
            Placed in: {card.bucket === 'current-state' ? 'Current State' : 
                       card.bucket === 'problem-opportunity' ? 'Problem / Opportunity' : 
                       'Ramifications'}
          </span>
          {onInsert && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onInsert(card.id);
              }}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              Insert
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Bucket Drop Zone Component
const BucketDropZone: React.FC<{
  bucket: BucketType;
  label: string;
  description: string;
  onDrop: (bucket: BucketType) => void;
  onTapSelect?: () => void;
  isMobile: boolean;
}> = ({ bucket, label, description, onDrop, onTapSelect, isMobile }) => {
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggedOver(true);
  };

  const handleDragLeave = () => {
    setIsDraggedOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggedOver(false);
    onDrop(bucket);
  };

  const bucketColors = {
    'current-state': 'primary',
    'problem-opportunity': 'sage',
    'ramifications': 'coral'
  } as const;

  const color = bucketColors[bucket];

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={isMobile ? onTapSelect : undefined}
      className={`min-h-[200px] p-6  border-2 border-dashed transition-all ${
        isDraggedOver
          ? `border-${color}-500 bg-${color}-100`
          : `border-${color}-200 bg-${color}-50`
      } ${isMobile ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <h4 className="font-semibold text-charcoal-900">{label}</h4>
        <button
          className="text-charcoal-400 hover:text-charcoal-600"
          title={getBucketTooltip(bucket)}
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      </div>
      <p className="text-sm text-charcoal-600 mb-4">{description}</p>
      {isMobile && (
        <p className="text-xs text-charcoal-500 italic">Tap to select</p>
      )}
    </div>
  );
};

function getBucketTooltip(bucket: BucketType): string {
  switch (bucket) {
    case 'current-state':
      return 'What is true? List concrete facts or observations. No interpretations. No judgments. No consequences.';
    case 'problem-opportunity':
      return 'What does the truth mean? Describe the underlying issue, friction, or opportunity created by the facts.';
    case 'ramifications':
      return 'Why does this matter? State the consequences of staying on the current path.';
  }
}

